import { client, unwrap } from '@/api/client';
import type { AuthenticationDto, UserDto } from '@/api/types';

export interface RegisterPayload {
    display_name: string;
    first_name?: string;
    last_name?: string;
    email: string;
    password: string;
    password_confirmation: string;
    device_name?: string;
    timezone?: string;
    accepts_health_disclaimer: true;
}

export interface LoginPayload {
    email: string;
    password: string;
    device_name?: string;
}

export async function register(payload: RegisterPayload): Promise<AuthenticationDto> {
    return unwrap(client.POST('/v1/auth/register', { body: payload }));
}

export async function login(payload: LoginPayload): Promise<AuthenticationDto> {
    return unwrap(client.POST('/v1/auth/login', { body: payload }));
}

export async function fetchCurrentUser(): Promise<UserDto> {
    const result = await unwrap(client.GET('/v1/auth/me'));

    return result.data;
}

export async function logout(): Promise<void> {
    await unwrap(client.POST('/v1/auth/logout'));
}

export async function logoutEverywhere(): Promise<void> {
    await unwrap(client.POST('/v1/auth/logout-all'));
}

export async function requestPasswordReset(email: string): Promise<{ message: string }> {
    return unwrap(client.POST('/v1/auth/forgot-password', { body: { email } }));
}

export interface ResetPasswordPayload {
    token: string;
    email: string;
    password: string;
    password_confirmation: string;
}

export async function resetPassword(payload: ResetPasswordPayload): Promise<{ message: string }> {
    return unwrap(client.POST('/v1/auth/reset-password', { body: payload }));
}

export async function resendVerificationEmail(): Promise<{ message: string }> {
    return unwrap(client.POST('/v1/auth/email/verification-notification'));
}

/**
 * A human-readable name for the token this browser is about to receive, so the
 * user can recognise the session later in their device list.
 *
 * Deliberately coarse: this is a label in a settings screen, not analytics, and
 * user-agent sniffing is only ever approximate.
 */
export function currentDeviceName(): string {
    if (typeof navigator === 'undefined') {
        return 'Webbrowser';
    }

    const ua = navigator.userAgent;

    // Order matters: Edge and Opera both also claim to be Chrome.
    const browsers: readonly [marker: string, name: string][] = [
        ['Edg/', 'Edge'],
        ['OPR/', 'Opera'],
        ['Firefox/', 'Firefox'],
        ['Chrome/', 'Chrome'],
        ['Safari/', 'Safari'],
    ];

    const platforms: readonly [marker: string, name: string][] = [
        ['iPhone', 'iOS'],
        ['iPad', 'iPadOS'],
        ['Android', 'Android'],
        ['Macintosh', 'macOS'],
        ['Windows', 'Windows'],
        ['Linux', 'Linux'],
    ];

    const browser = browsers.find(([marker]) => ua.includes(marker))?.[1] ?? 'Browser';
    const platform = platforms.find(([marker]) => ua.includes(marker))?.[1] ?? 'onbekend systeem';

    return `${browser} op ${platform}`;
}
